/**
 * Centralized Market Data API Service (FMP Ultimate)
 * Migrated from Twelve Data → Financial Modeling Prep (FMP) Ultimate $149/mo
 * Uses the FMP **Stable** API (all v3/v4 legacy endpoints are deprecated).
 *
 * ★ SECURITY: All FMP calls now route through our Cloud Function proxy
 *   (`fmpProxy`). The API key lives ONLY server-side (functions/.env) — it is
 *   never shipped to the browser. The proxy requires a Firebase Auth token and
 *   rate-limits per IP. Response formats remain IDENTICAL, so no consumer code
 *   changes are needed.
 */

import { resolveSymbolAlias } from '../utils/symbolAlias';
import { getAuth } from 'firebase/auth';

// FMP calls go through this Cloud Function proxy (key stays server-side).
const FMP_PROXY_URL = 'https://us-central1-investport-pro.cloudfunctions.net/fmpProxy';

// ─────────────────────────────────────────────────────────────────────────────
// ★ Client-side API Audit Logger (kept for Admin panel compatibility)
// ─────────────────────────────────────────────────────────────────────────────
const TD_CLIENT_LOG_KEY = 'portpro_td_api_log';

function logClientCall(path, params, status, durationMs) {
  try {
    const symbolParam = params.symbol || params.query || '';
    const symbolCount = symbolParam ? symbolParam.split(',').length : 1;

    const entry = {
      t: new Date().toISOString(),
      path,
      symbols: symbolParam.slice(0, 100),
      symbolCount,
      credits: symbolCount,
      status,
      ms: durationMs,
      caller: '',
    };

    console.log(`[FMP-API] ${path} | ${symbolCount} sym | ${status} | ${durationMs}ms`, symbolParam.slice(0, 50));

    const existing = JSON.parse(sessionStorage.getItem(TD_CLIENT_LOG_KEY) || '[]');
    existing.push(entry);
    if (existing.length > 200) existing.splice(0, existing.length - 200);
    sessionStorage.setItem(TD_CLIENT_LOG_KEY, JSON.stringify(existing));
  } catch (e) { /* non-fatal */ }
}

// Export for admin panel to read
export function getTdClientLog() {
  try {
    return JSON.parse(sessionStorage.getItem(TD_CLIENT_LOG_KEY) || '[]');
  } catch { return []; }
}

export function clearTdClientLog() {
  sessionStorage.removeItem(TD_CLIENT_LOG_KEY);
}

// ─────────────────────────────────────────────────────────────────────────────
// ★ Symbol Format Helpers
// TD uses: BTC/USD, EUR/USD, XAU/USD, USD/THB
// FMP uses: BTCUSD, EURUSD, XAUUSD, USDTHB (no slash)
//
// IMPORTANT: On FMP the bare ticker `BTC` is "Grayscale Bitcoin Mini Trust"
// (an AMEX ETF), NOT Bitcoin. Bitcoin is `BTCUSD`. We normalize known crypto
// tickers to the canonical <COIN>USD form so chart/quote/technicals all resolve
// to the actual cryptocurrency instead of a same-named equity/ETF.
// ─────────────────────────────────────────────────────────────────────────────
const FMP_CRYPTO_COINS = new Set([
  'BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA', 'DOGE', 'AVAX', 'DOT', 'LINK',
  'MATIC', 'UNI', 'SHIB', 'LTC', 'BCH', 'XLM', 'ATOM', 'NEAR', 'OP', 'ARB',
  'APT', 'SUI', 'SEI', 'TIA', 'INJ', 'PEPE', 'WIF', 'JUP', 'RENDER', 'FET',
]);

function toFmpSymbol(tdSymbol) {
  if (!tdSymbol) return '';
  if (tdSymbol.includes(',')) {
    return tdSymbol.split(',').map(s => toFmpSymbol(s.trim())).join(',');
  }
  const noSlash = tdSymbol.replace('/', '');
  const upper = noSlash.toUpperCase();

  // Resolve aliases (e.g. NAS100 -> ^NDX, WTI -> CLUSD)
  const resolved = resolveSymbolAlias(upper);

  // Normalize crypto tickers → <COIN>USD (exact base match to avoid colliding
  // with real equities). Strips trailing USD/USDT/USDC then re-appends USD.
  let base = resolved;
  if (base.endsWith('USDT') || base.endsWith('USDC')) base = base.slice(0, -4);
  else if (base.endsWith('USD')) base = base.slice(0, -3);
  if (FMP_CRYPTO_COINS.has(base)) {
    return `${base}USD`;
  }

  return resolved;
}

// ─────────────────────────────────────────────────────────────────────────────
// ★ In-flight request deduplication
// When multiple callers request the exact same endpoint+params simultaneously
// (e.g., getStatistics + getKeyMetrics both calling /ratios-ttm?symbol=INTC),
// they share a single Promise. Entries expire after 5 seconds.
// ─────────────────────────────────────────────────────────────────────────────
const _inflight = new Map(); // cacheKey → { promise, ts }
const INFLIGHT_TTL = 5000;

// ─────────────────────────────────────────────────────────────────────────────
// ★ Generic FMP Stable fetch
// All Stable API responses are wrapped: { value: [...], Count: N }
// This function automatically unwraps .value so callers get a plain array.
// ─────────────────────────────────────────────────────────────────────────────
async function fmpFetch(endpoint, params = {}) {
  // Route via the proxy: the real FMP path goes in the `endpoint` query param.
  const queryParts = [`endpoint=${encodeURIComponent(endpoint)}`];
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      queryParts.push(`${key}=${encodeURIComponent(value)}`);
    }
  }
  const url = `${FMP_PROXY_URL}?${queryParts.join('&')}`;

  // Dedup: reuse in-flight request for the same URL within TTL
  const now = Date.now();
  const existing = _inflight.get(url);
  if (existing && (now - existing.ts) < INFLIGHT_TTL) {
    return existing.promise;
  }

  const doFetch = (async () => {
    const t0 = Date.now();
    // Attach Firebase Auth token so the proxy can verify the caller.
    const headers = {};
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
      }
    } catch (e) { /* no auth available → proxy will reject with 401 */ }

    const res = await fetch(url, { headers });
    const durationMs = Date.now() - t0;

    logClientCall(endpoint, params, res.ok ? 'ok' : `err:${res.status}`, durationMs);

    if (!res.ok) {
      if (res.status === 404) return []; // No data — don't crash callers
      throw new Error(`FMP API error: ${res.status}`);
    }
    const json = await res.json();
    // Unwrap Stable API envelope
    if (json && typeof json === 'object' && Array.isArray(json.value)) {
      return json.value;
    }
    return json;
  })();

  _inflight.set(url, { promise: doFetch, ts: now });
  // Cleanup after TTL to prevent memory leak
  // .catch(() => {}) suppresses unhandled rejection on this cleanup branch
  doFetch.finally(() => {
    setTimeout(() => _inflight.delete(url), INFLIGHT_TTL);
  }).catch(() => { });

  return doFetch;
}

// Keep legacy export name for compatibility (some code might reference it)
export async function twelveDataFetch(path, params = {}) {
  return fmpFetch(path, params);
}

// ─────────────────────────────────────────────────
// Market Data
// ─────────────────────────────────────────────────

/**
 * Get real-time quote for one or more symbols (batch supported)
 * Returns data in Twelve Data format: { close, percent_change, extended_price, ... }
 * @param {string} symbol - comma-separated symbols e.g. "AAPL,TSLA,MSFT"
 * @returns {Promise<Object>} Quote data
 */
export async function getQuote(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  const isBatch = symbol.includes(',');

  // Stable API: single → /quote?symbol=AAPL
  //             batch  → /batch-quote?symbols=AAPL,TSLA (plural, plain array)
  const data = isBatch
    ? await fmpFetch('/batch-quote', { symbols: fmpSymbol })
    : await fmpFetch('/quote', { symbol: fmpSymbol });

  if (!Array.isArray(data) || data.length === 0) {
    return { code: 404, message: 'No data' };
  }

  // Single symbol → flat object matching TD format
  if (!isBatch) {
    return mapFmpQuoteToTd(data[0]);
  }

  // Batch → { "AAPL": {...}, "BTC/USD": {...} } keyed by ORIGINAL TD symbol
  const tdSymbols = symbol.split(',').map(s => s.trim());
  const result = {};
  tdSymbols.forEach((tdSym, i) => {
    const fmpSym = toFmpSymbol(tdSym);
    const q = data.find(d => d.symbol === fmpSym) || data[i];
    if (q) result[tdSym] = mapFmpQuoteToTd(q);
  });
  return result;
}

function mapFmpQuoteToTd(q) {
  if (!q || q.price == null) return { code: 404, message: 'No data' };
  return {
    symbol: q.symbol || '',
    name: q.name || '',
    exchange: q.exchange || '',
    currency: 'USD',
    open: String(q.open ?? 0),
    high: String(q.dayHigh ?? 0),
    low: String(q.dayLow ?? 0),
    close: String(q.price ?? 0),
    volume: String(q.volume ?? 0),
    previous_close: String(q.previousClose ?? 0),
    change: String(q.change ?? 0),
    percent_change: String(q.changePercentage ?? 0),
    average_volume: String(q.avgVolume ?? 0),
    fifty_two_week: {
      low: String(q.yearLow ?? 0),
      high: String(q.yearHigh ?? 0),
    },
    // Extended hours — FMP includes pre/aftermarket in main quote
    extended_price: q.preMarketPrice ? String(q.preMarketPrice) : (q.afterHoursPrice ? String(q.afterHoursPrice) : null),
    extended_change: q.preMarketChange ? String(q.preMarketChange) : (q.afterHoursChange ? String(q.afterHoursChange) : null),
    extended_percent_change: q.preMarketChangePercentage ? String(q.preMarketChangePercentage) : (q.afterHoursChangePercentage ? String(q.afterHoursChangePercentage) : null),
  };
}

/**
 * Batch fetch extended hours prices for multiple symbols using batch endpoints.
 * Uses /batch-aftermarket-trade and /batch-premarket-trade (single request each).
 * @param {string[]} symbols
 * @param {Object} closePrices - { symbol: closePrice }
 * @returns {Object} { symbol: { price, change, changePct, session } }
 */
export async function getExtendedHoursBatch(symbols, closePrices = {}) {
  if (!symbols || symbols.length === 0) return {};
  const fmpSymbols = symbols.map(toFmpSymbol).join(',');
  const results = {};

  // Map FMP symbol back to original
  const fmpToOrig = {};
  symbols.forEach(s => { fmpToOrig[toFmpSymbol(s)] = s; });

  // Try both endpoints in parallel — aftermarket + premarket
  const [afterData, preData] = await Promise.all([
    fmpFetch('/batch-aftermarket-trade', { symbols: fmpSymbols }).catch(() => []),
    fmpFetch('/batch-premarket-trade', { symbols: fmpSymbols }).catch(() => []),
  ]);

  const process = (data, session) => {
    if (!Array.isArray(data)) return;
    data.forEach(item => {
      if (!item?.symbol || !item?.price) return;
      const origSym = fmpToOrig[item.symbol] || item.symbol;
      if (results[origSym]) return; // aftermarket takes priority
      const extP = Number(item.price);
      const cp = Number(closePrices[origSym]) || 0;
      const chg = cp ? extP - cp : 0;
      const pct = cp ? (chg / cp) * 100 : 0;
      results[origSym] = { price: extP, change: chg, changePct: pct, session };
    });
  };

  process(afterData, 'POST_MARKET');
  process(preData, 'PRE_MARKET');
  return results;
}

/**
 * Get latest price only (lightest endpoint)
 * @param {string} symbol
 */
export async function getPrice(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  // Stable API has no quote-short; reuse /quote (lightweight enough)
  const data = await fmpFetch('/quote', { symbol: fmpSymbol });
  if (Array.isArray(data) && data.length > 0) {
    return { price: String(data[0].price ?? 0) };
  }
  return { price: '0' };
}

/**
 * Get time series (historical OHLCV data)
 * Returns data in Twelve Data format: { values: [{ datetime, open, high, low, close, volume }] }
 * TD returns newest-first; we maintain that convention.
 * @param {string} symbol
 * @param {string} interval - e.g. '1min','5min','15min','30min','1h','1day','1week','1month'
 * @param {number|null} outputsize - number of data points (default 30)
 * @param {Object} [extraParams] - optional { start_date, end_date }
 */
export async function getTimeSeries(symbol, interval = '1day', outputsize = 30, extraParams = {}) {
  const fmpSymbol = toFmpSymbol(symbol);
  const { start_date, end_date } = extraParams;

  let rawData;

  if (['1min', '5min', '15min', '30min'].includes(interval)) {
    // Stable intraday: /historical-chart/{interval}?symbol=AAPL
    const params = { symbol: fmpSymbol };
    if (start_date) params.from = start_date;
    if (end_date) params.to = end_date;
    rawData = await fmpFetch(`/historical-chart/${interval}`, params);
  } else if (interval === '1h' || interval === '1hour') {
    const params = { symbol: fmpSymbol };
    if (start_date) params.from = start_date;
    if (end_date) params.to = end_date;
    rawData = await fmpFetch('/historical-chart/1hour', params);
  } else {
    // Daily, weekly, monthly → /historical-price-eod/full?symbol=AAPL
    const params = { symbol: fmpSymbol };
    if (start_date) params.from = start_date;
    if (end_date) params.to = end_date;
    rawData = await fmpFetch('/historical-price-eod/full', params);
  }

  if (!Array.isArray(rawData) || rawData.length === 0) {
    return { values: [], meta: { symbol } };
  }

  // Aggregate to weekly or monthly if needed
  if (interval === '1week') {
    rawData = aggregateToWeekly(rawData);
  } else if (interval === '1month') {
    rawData = aggregateToMonthly(rawData);
  }

  // Limit output size (data arrives newest-first)
  if (outputsize && rawData.length > outputsize) {
    rawData = rawData.slice(0, outputsize);
  }

  // Convert FMP → TD format (newest first)
  const values = rawData.map(bar => ({
    datetime: bar.date || '',
    open: String(bar.open ?? 0),
    high: String(bar.high ?? 0),
    low: String(bar.low ?? 0),
    close: String(bar.close ?? bar.adjClose ?? 0),
    volume: String(bar.volume ?? 0),
  }));

  return { values, meta: { symbol, interval } };
}

// Aggregate daily bars → weekly OHLCV (input is newest-first)
function aggregateToWeekly(dailyBars) {
  if (!dailyBars || dailyBars.length === 0) return [];
  // Sort oldest-first for processing
  const sorted = [...dailyBars].reverse();
  const weeks = [];
  let current = null;

  sorted.forEach(bar => {
    const d = new Date(bar.date);
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() - d.getDay()); // Sunday of that week
    const weekKey = weekStart.toISOString().slice(0, 10);

    if (!current || current._weekKey !== weekKey) {
      if (current) weeks.push(current);
      current = {
        _weekKey: weekKey,
        date: bar.date,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
        volume: bar.volume || 0,
      };
    } else {
      current.high = Math.max(current.high, bar.high);
      current.low = Math.min(current.low, bar.low);
      current.close = bar.close; // Last day's close
      current.volume = (current.volume || 0) + (bar.volume || 0);
      current.date = bar.date; // Use last trading day as the bar date
    }
  });
  if (current) weeks.push(current);

  // Return newest-first
  return weeks.reverse();
}

// Aggregate daily bars → monthly OHLCV (input is newest-first)
function aggregateToMonthly(dailyBars) {
  if (!dailyBars || dailyBars.length === 0) return [];
  const sorted = [...dailyBars].reverse();
  const months = [];
  let current = null;

  sorted.forEach(bar => {
    const monthKey = bar.date ? bar.date.slice(0, 7) : ''; // "2026-06"

    if (!current || current._monthKey !== monthKey) {
      if (current) months.push(current);
      current = {
        _monthKey: monthKey,
        date: bar.date,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
        volume: bar.volume || 0,
      };
    } else {
      current.high = Math.max(current.high, bar.high);
      current.low = Math.min(current.low, bar.low);
      current.close = bar.close;
      current.volume = (current.volume || 0) + (bar.volume || 0);
      current.date = bar.date;
    }
  });
  if (current) months.push(current);

  return months.reverse();
}

/**
 * Get end-of-day price
 * @param {string} symbol
 */
export async function getEodPrice(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/quote', { symbol: fmpSymbol });
  if (Array.isArray(data) && data.length > 0) {
    const q = data[0];
    return {
      symbol: q.symbol,
      exchange: q.exchange || '',
      close: String(q.price ?? 0),
      date: new Date().toISOString().slice(0, 10),
    };
  }
  return { close: '0' };
}

// ─────────────────────────────────────────────────
// Reference Data / Discovery
// ─────────────────────────────────────────────────

/**
 * Search for symbols (stocks, ETFs, forex, crypto)
 * Returns in TD format: { data: [{ symbol, instrument_name, exchange, ... }] }
 * @param {string} query
 * @param {number} outputsize - max results (default 20)
 */
export async function symbolSearch(query, outputsize = 20) {
  // Stable API: /search-name?query=apple&limit=20  (searches by company name)
  const data = await fmpFetch('/search-name', { query, limit: outputsize });
  if (!Array.isArray(data)) return { data: [] };

  const mapped = data.map(item => ({
    symbol: item.symbol || '',
    instrument_name: item.name || '',
    exchange: item.exchange || item.exchangeFullName || '',
    instrument_type: 'Common Stock',
    country: 'United States',
    currency: item.currency || 'USD',
  }));
  return { data: mapped };
}

/**
 * Get all stock symbols for an exchange
 * Returns array of { symbol, name, type, exchange }
 * @param {string} exchange - e.g. 'US', 'NYSE', 'NASDAQ'
 * @param {string} type - e.g. 'Common Stock', 'ETF'
 */
export async function getStocksList(exchange = 'US', type) {
  // Stable API: /stock-list  (returns {symbol, companyName} — no exchange field)
  const data = await fmpFetch('/stock-list', {});
  if (!Array.isArray(data)) return { data: [] };

  let filtered = data;

  // FMP Stable only returns { symbol, companyName } with no exchange info.
  // If the data has exchange fields, filter by exchange; otherwise use the full list
  // but skip obviously non-US symbols (contain dots like BRK.A, have very long symbols, etc.)
  const hasExchange = data.length > 0 && (data[0].exchangeShortName || data[0].exchange);
  if (exchange && hasExchange) {
    const exUpper = exchange.toUpperCase();
    filtered = data.filter(item => {
      const ex = (item.exchangeShortName || item.exchange || '').toUpperCase();
      if (exUpper === 'US') return ['NYSE', 'NASDAQ', 'AMEX', 'NYSEMKT', 'NYSEARCA'].includes(ex);
      return ex.includes(exUpper);
    });
  }

  if (type && hasExchange) {
    const typeLower = type.toLowerCase();
    filtered = filtered.filter(item => (item.type || '').toLowerCase().includes(typeLower));
  }

  const mapped = filtered
    .filter(item => item.symbol && item.symbol.length <= 6 && !item.symbol.includes('.'))
    .map(item => ({
      symbol: item.symbol,
      name: item.companyName || item.name || '',
      type: item.type || 'Common Stock',
      exchange: item.exchangeShortName || item.exchange || '',
    }));

  return { data: mapped };
}

/**
 * Get market state (open/closed) for exchanges
 * Returns in TD format: [{ name, code, is_market_open }]
 * @param {string} exchange - exchange code
 */
export async function getMarketState(exchange) {
  // Stable API: /exchange-market-hours?exchange=NASDAQ
  // Returns: [{ exchange, name, openingHour, closingHour, timezone, isMarketOpen }]
  const data = await fmpFetch('/exchange-market-hours', { exchange: exchange || 'NASDAQ' });

  if (Array.isArray(data) && data.length > 0) {
    return data.map(item => ({
      name: item.name || item.exchange || '',
      code: item.exchange || exchange || 'NASDAQ',
      country: 'United States',
      is_market_open: item.isMarketOpen || false,
    }));
  }

  return [{ name: exchange || 'US', code: exchange || 'NASDAQ', is_market_open: false }];
}

// ─────────────────────────────────────────────────
// Fundamentals
// ─────────────────────────────────────────────────

/**
 * Get company profile
 * Returns in TD format: { name, description, sector, industry, CEO, ... }
 * @param {string} symbol
 */
export async function getProfile(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  // Stable API: /profile?symbol=AAPL
  const data = await fmpFetch('/profile', { symbol: fmpSymbol });
  if (!Array.isArray(data) || data.length === 0) return {};

  const p = data[0];
  return {
    symbol: p.symbol || '',
    name: p.companyName || '',
    exchange: p.exchange || '',
    sector: p.sector || '',
    industry: p.industry || '',
    employees: p.fullTimeEmployees || 0,
    CEO: p.ceo || '',
    description: p.description || '',
    type: p.isEtf ? 'ETF' : 'Common Stock',
    website: p.website || '',
    logo: p.image || '',
  };
}

/**
 * Get company logo URL
 * FMP includes logo in profile; we return it in TD format: { url }
 * @param {string} symbol
 */
export async function getLogo(symbol) {
  // FMP provides logos at a predictable URL pattern
  const fmpSymbol = toFmpSymbol(symbol);
  return { url: `https://financialmodelingprep.com/image-stock/${fmpSymbol}.png` };
}

/**
 * Get key statistics (P/E, Market Cap, Dividend Yield, 52wk High/Low, etc.)
 * Returns in TD format matching what DividendCalculatorCard expects:
 * { statistics: { dividends_and_splits: { forward_annual_dividend_yield, ... }, ... } }
 * @param {string} symbol
 */
export async function getStatistics(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);

  // Stable API: /ratios-ttm has dividend yield, P/E, etc.
  //             /quote has 52-wk, avg prices, etc.
  const [ratiosRes, quoteRes] = await Promise.all([
    fmpFetch('/ratios-ttm', { symbol: fmpSymbol }),
    fmpFetch('/quote', { symbol: fmpSymbol }),
  ]);

  const ratios = Array.isArray(ratiosRes) && ratiosRes.length > 0 ? ratiosRes[0] : {};
  const quote = Array.isArray(quoteRes) && quoteRes.length > 0 ? quoteRes[0] : {};

  return {
    statistics: {
      valuations: {
        market_capitalization: quote.marketCap || 0,
        enterprise_value: ratios.enterpriseValueTTM || 0,
        trailing_pe: ratios.priceToEarningsRatioTTM || 0,
        forward_pe: 0,
        peg_ratio: ratios.priceToEarningsGrowthRatioTTM || 0,
        price_to_sales_ttm: ratios.priceToSalesRatioTTM || 0,
        price_to_book_mrq: ratios.priceToBookRatioTTM || 0,
      },
      financials: {
        revenue_ttm: ratios.revenuePerShareTTM || 0,
        net_income_ttm: ratios.netIncomePerShareTTM || 0,
        diluted_eps_ttm: ratios.netIncomePerShareTTM || 0,
        return_on_equity_ttm: ratios.returnOnEquityTTM || 0,
        return_on_assets_ttm: ratios.returnOnAssetsTTM || 0,
        profit_margin: ratios.netProfitMarginTTM || 0,
        operating_margin: ratios.operatingProfitMarginTTM || 0,
      },
      dividends_and_splits: {
        forward_annual_dividend_yield: ratios.dividendYieldTTM || 0,
        forward_annual_dividend_rate: ratios.dividendPerShareTTM || 0,
        trailing_annual_dividend_yield: ratios.dividendYieldTTM || 0,
        trailing_annual_dividend_rate: ratios.dividendPerShareTTM || 0,
        payout_ratio: ratios.dividendPayoutRatioTTM || 0,
      },
      stock_price_summary: {
        fifty_two_week_low: quote.yearLow || 0,
        fifty_two_week_high: quote.yearHigh || 0,
        fifty_day_average: quote.priceAvg50 || 0,
        two_hundred_day_average: quote.priceAvg200 || 0,
        beta: ratios.betaTTM || 0,
      },
    },
    // Real exchange + name straight from the quote (never hardcoded)
    meta: {
      exchange: quote.exchange || '',
      name: quote.name || '',
      price: quote.price ?? null,
    },
  };
}

/**
 * Get forward EPS from analyst consensus estimates (annual).
 * Picks the nearest UPCOMING fiscal-year estimate (date >= today) with a
 * positive consensus EPS. Returns null if no usable estimate exists.
 * Source: FMP /analyst-estimates?period=annual (real analyst consensus).
 * @param {string} symbol
 * @returns {Promise<{eps:number, fiscalDate:string}|null>}
 */
export async function getForwardEps(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  let data;
  try {
    data = await fmpFetch('/analyst-estimates', { symbol: fmpSymbol, period: 'annual', limit: 10 });
  } catch {
    return null;
  }
  if (!Array.isArray(data) || data.length === 0) return null;
  const today = new Date().toISOString().slice(0, 10);
  // Estimates with a fiscal-year-end date in the future, soonest first
  const upcoming = data
    .filter(e => (e.date || '') >= today && Number.isFinite(Number(e.epsAvg)))
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const pick = upcoming[0];
  if (!pick) return null;
  const eps = Number(pick.epsAvg);
  if (!Number.isFinite(eps) || eps === 0) return null;
  return { eps, fiscalDate: pick.date };
}

/**
 * Get stock split history.
 * Returns array of { date, numerator, denominator } newest-first.
 * Used to flag "split-adjusted data" when a split occurred recently.
 * @param {string} symbol
 */
export async function getStockSplits(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  let data;
  try {
    data = await fmpFetch('/splits', { symbol: fmpSymbol });
  } catch {
    return [];
  }
  if (!Array.isArray(data)) return [];
  return data.map(s => ({
    date: s.date || '',
    numerator: s.numerator ?? null,
    denominator: s.denominator ?? null,
  }));
}

/**
 * Get financial growth (YoY revenue growth, EPS growth, etc.)
 * @param {string} symbol
 * @returns {{ revenueGrowth, epsdilutedGrowth, operatingIncomeGrowth, ... }}
 */
export async function getFinancialGrowth(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/financial-growth', { symbol: fmpSymbol });
  if (Array.isArray(data) && data.length > 0) return data[0];
  return null;
}

/**
 * Get dividend history
 * Returns in TD format: { dividends: [{ ex_date, amount, ... }] }
 * @param {string} symbol
 * @param {string} range - e.g. '1y', '2y', '5y'
 */
export async function getDividends(symbol, range) {
  const fmpSymbol = toFmpSymbol(symbol);
  // Stable API: /dividends?symbol=AAPL
  let dividends = await fmpFetch('/dividends', { symbol: fmpSymbol });
  if (!Array.isArray(dividends)) dividends = [];

  // Filter by range
  if (range && dividends.length > 0) {
    const years = parseInt(range) || 1;
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - years);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    dividends = dividends.filter(d => (d.date || '') >= cutoffStr);
  }

  return {
    dividends: dividends.map(d => ({
      ex_date: d.date || '',
      amount: d.dividend || d.adjDividend || 0,
      record_date: d.recordDate || '',
      payment_date: d.paymentDate || '',
      declaration_date: d.declarationDate || '',
    })),
    meta: { symbol },
  };
}

/**
 * Get upcoming dividends calendar
 * @param {string} symbol - optional
 * @param {string} start_date - optional, YYYY-MM-DD
 * @param {string} end_date - optional, YYYY-MM-DD
 */
export async function getDividendsCalendar(symbol, start_date, end_date) {
  const params = {};
  if (start_date) params.from = start_date;
  if (end_date) params.to = end_date;

  // Stable API: /dividends-calendar?from=...&to=...
  const data = await fmpFetch('/dividends-calendar', params);
  if (!Array.isArray(data)) return { dividends: [] };

  let filtered = data;
  if (symbol) {
    const syms = symbol.split(',').map(s => toFmpSymbol(s.trim()).toUpperCase());
    filtered = data.filter(d => syms.includes((d.symbol || '').toUpperCase()));
  }

  return {
    dividends: filtered.map(d => ({
      symbol: d.symbol || '',
      ex_date: d.date || '',
      amount: d.dividend || d.adjDividend || 0,
      record_date: d.recordDate || '',
      payment_date: d.paymentDate || '',
      declaration_date: d.declarationDate || '',
    })),
  };
}

/**
 * Get earnings history (EPS actual vs estimate)
 * Returns in TD format: { earnings: [{ date, eps, eps_estimate, ... }] }
 * @param {string} symbol
 */
export async function getEarnings(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  // Stable API: /earnings?symbol=AAPL
  const data = await fmpFetch('/earnings', { symbol: fmpSymbol });
  if (!Array.isArray(data)) return { earnings: [] };

  return {
    earnings: data.map(e => ({
      date: e.date || '',
      eps: e.epsActual || 0,
      eps_estimate: e.epsEstimated || 0,
      revenue: e.revenueActual || 0,
      revenue_estimate: e.revenueEstimated || 0,
      surprise: e.epsActual && e.epsEstimated ? (e.epsActual - e.epsEstimated) : 0,
      surprise_percent: e.epsActual && e.epsEstimated && e.epsEstimated !== 0
        ? ((e.epsActual - e.epsEstimated) / Math.abs(e.epsEstimated)) * 100
        : 0,
    })),
    meta: { symbol },
  };
}

/**
 * Get upcoming earnings calendar
 * Returns in TD format: { earnings: [{ symbol, date, eps_estimate, ... }] }
 */
export async function getEarningsCalendar() {
  const today = new Date().toISOString().slice(0, 10);
  const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  // Stable API: /earnings-calendar?from=...&to=...
  const data = await fmpFetch('/earnings-calendar', { from: today, to: nextWeek });
  if (!Array.isArray(data)) return { earnings: [] };

  return {
    earnings: data.map(e => ({
      symbol: e.symbol || '',
      date: e.date || '',
      eps_estimate: e.epsEstimated || 0,
      revenue_estimate: e.revenueEstimated || 0,
      time: e.time || '',
    })),
  };
}

/**
 * Get press releases / news for a company
 * Returns in TD format: { press_releases: [{ title, datetime, source, url }] }
 * @param {string} symbol
 * @param {number} size - number of results
 */
export async function getPressReleases(symbol, size = 10) {
  const fmpSymbol = toFmpSymbol(symbol);
  // Stable API: /news/stock?symbols=AAPL&limit=10 (must use plural 'symbols')
  const data = await fmpFetch('/news/stock', { symbols: fmpSymbol, limit: size });
  if (!Array.isArray(data)) return { press_releases: [] };

  return {
    press_releases: data.map(item => ({
      title: item.title || '',
      datetime: item.publishedDate || '',
      source: item.site || item.publisher || '',
      url: item.url || '',
      text: item.text || '',
      image: item.image || '',
    })),
    meta: { symbol },
  };
}

// ─────────────────────────────────────────────────
// Currencies
// ─────────────────────────────────────────────────

/**
 * Get exchange rate between two currencies
 * TD format: { symbol: "USD/THB", rate: 35.45, ... }
 * @param {string} symbol - e.g. 'USD/THB'
 */
export async function getExchangeRate(symbol) {
  const fmpSymbol = toFmpSymbol(symbol); // "USD/THB" → "USDTHB"
  // Stable API: /quote?symbol=USDTHB  (works for forex pairs)
  const data = await fmpFetch('/quote', { symbol: fmpSymbol });

  if (Array.isArray(data) && data.length > 0) {
    return {
      symbol,
      rate: data[0].price || 0,
      timestamp: Math.floor(Date.now() / 1000),
    };
  }
  return { symbol, rate: 0 };
}

/**
 * Convert currency amount
 * @param {string} symbol - e.g. 'USD/THB'
 * @param {number} amount
 */
export async function getCurrencyConversion(symbol, amount) {
  const rateData = await getExchangeRate(symbol);
  const rate = parseFloat(rateData.rate) || 0;
  const numAmount = parseFloat(amount) || 0;

  return {
    symbol,
    rate,
    amount: numAmount,
    result: rate * numAmount,
    timestamp: Math.floor(Date.now() / 1000),
  };
}

/**
 * Get API usage stats
 * FMP doesn't have this endpoint — return synthetic data
 */
export async function getApiUsage() {
  return {
    current_usage: 0,
    plan_limit: 999999,
    provider: 'FMP Ultimate',
    note: 'FMP uses rate limit (3000 calls/min) instead of daily credits',
  };
}

/**
 * Get Income Statement (quarterly, last 8 quarters)
 * @param {string} symbol
 * @param {string} period - 'quarter' or 'annual'
 * @param {number} limit - number of periods
 */
export async function getIncomeStatement(symbol, period = 'quarter', limit = 8) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/income-statement', { symbol: fmpSymbol, period, limit });
  if (!Array.isArray(data)) return [];
  return data.map(item => ({
    date: item.date || '',
    period: item.period || '',
    revenue: item.revenue || 0,
    grossProfit: item.grossProfit || 0,
    operatingIncome: item.operatingIncome || 0,
    netIncome: item.netIncome || 0,
    eps: item.eps || 0,
    epsDiluted: item.epsdiluted || item.epsDiluted || 0,
    grossProfitRatio: item.grossProfitRatio || 0,
    operatingIncomeRatio: item.operatingIncomeRatio || 0,
    netIncomeRatio: item.netIncomeRatio || 0,
    weightedAverageShsOutDil: item.weightedAverageShsOutDil || 0,
  }));
}

/**
 * Get upcoming earnings date for a specific symbol
 * @param {string} symbol
 */
export async function getEarningsCalendarForSymbol(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  // Get historical + upcoming earnings
  const data = await fmpFetch('/earnings', { symbol: fmpSymbol, limit: 8 });
  if (!Array.isArray(data)) return [];
  return data.map(item => ({
    date: item.date || '',
    eps: item.eps ?? null,
    epsEstimated: item.epsEstimated ?? null,
    revenue: item.revenue ?? null,
    revenueEstimated: item.revenueEstimated ?? null,
    fiscalDateEnding: item.fiscalDateEnding || '',
    surprise: item.eps != null && item.epsEstimated != null
      ? ((item.eps - item.epsEstimated) / Math.abs(item.epsEstimated || 1) * 100)
      : null,
    beat: item.eps != null && item.epsEstimated != null ? item.eps > item.epsEstimated : null,
  }));
}

/**
 * Get analyst price targets
 * @param {string} symbol
 */
export async function getPriceTarget(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  // Try price-target-consensus first, fallback to price-target-summary
  let data;
  try {
    data = await fmpFetch('/price-target-consensus', { symbol: fmpSymbol });
  } catch {
    try {
      data = await fmpFetch('/price-target-summary', { symbol: fmpSymbol });
    } catch { return null; }
  }
  // Handle both array and object responses
  const item = Array.isArray(data) ? data[0] : data;
  if (!item) return null;
  return {
    targetHigh: item.targetHigh || item.lastMonthAvgPriceTarget || 0,
    targetLow: item.targetLow || item.lastQuarterAvgPriceTarget || 0,
    targetConsensus: item.targetConsensus || item.targetMedian || item.lastMonthAvgPriceTarget || 0,
    targetMedian: item.targetMedian || item.targetConsensus || 0,
  };
}

/**
 * Get analyst rating/recommendation
 * @param {string} symbol
 */
export async function getAnalystRating(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  let data;
  try {
    data = await fmpFetch('/rating', { symbol: fmpSymbol });
  } catch { return null; }
  // Handle both array and object
  const item = Array.isArray(data) ? data[0] : data;
  if (!item || !item.rating) return null;
  return {
    rating: item.rating || '',
    ratingScore: item.ratingScore || 0,
    ratingRecommendation: item.ratingRecommendation || '',
    ratingDetailsDCFScore: item.ratingDetailsDCFScore || 0,
    ratingDetailsROEScore: item.ratingDetailsROEScore || 0,
    ratingDetailsROAScore: item.ratingDetailsROAScore || 0,
    ratingDetailsDEScore: item.ratingDetailsDEScore || 0,
    ratingDetailsPEScore: item.ratingDetailsPEScore || 0,
    ratingDetailsPBScore: item.ratingDetailsPBScore || 0,
  };
}

/**
 * Get DCF (Discounted Cash Flow) fair value
 * @param {string} symbol
 */
export async function getDcfValue(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/discounted-cash-flow', { symbol: fmpSymbol });
  if (!Array.isArray(data) || data.length === 0) return null;
  const item = data[0];
  return {
    dcf: item.dcf || 0,
    price: item.stockPrice || item.price || 0,
    date: item.date || '',
  };
}

/**
 * Get Key Metrics TTM
 * @param {string} symbol
 */
export async function getKeyMetrics(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  // Fetch profile, ratios-ttm, shares-float, and latest annual income in parallel
  const [profile, ratios, sharesFloat, incomeAnnual] = await Promise.all([
    fmpFetch('/profile', { symbol: fmpSymbol }).then(d => (Array.isArray(d) ? d[0] : d)).catch(() => null),
    fmpFetch('/ratios-ttm', { symbol: fmpSymbol }).then(d => (Array.isArray(d) ? d[0] : d)).catch(() => null),
    fmpFetch('/shares-float', { symbol: fmpSymbol }).then(d => (Array.isArray(d) ? d[0] : d)).catch(() => null),
    fmpFetch('/income-statement', { symbol: fmpSymbol, period: 'annual', limit: 1 }).then(d => (Array.isArray(d) ? d[0] : d)).catch(() => null),
  ]);
  if (!profile && !ratios) return null;
  const p = profile || {};
  const r = ratios || {};
  const sf = sharesFloat || {};
  const inc = incomeAnnual || {};
  return {
    // Key Stats (TradingView-style)
    volume: p.volume || 0,
    avgVolume: p.averageVolume || 0,
    marketCap: p.marketCap || 0,
    beta: p.beta || 0,
    dividendYield: r.dividendYieldTTM || 0,
    peRatio: r.priceToEarningsRatioTTM || 0,
    pbRatio: r.priceToBookRatioTTM || 0,
    epsBasic: inc.eps || 0,
    netIncomeFY: inc.netIncome || 0,
    revenueFY: inc.revenue || 0,
    floatShares: sf.floatShares || 0,
    outstandingShares: sf.outstandingShares || 0,
    // Extra ratios
    evToEbitda: r.evToEBITDATTM || 0,
    currentRatio: r.currentRatioTTM || 0,
    debtToEquity: r.debtToEquityRatioTTM || 0,
    earningsYield: r.earningsYieldTTM || 0,
    freeCashFlowYield: r.freeCashFlowYieldTTM || 0,
    dividendPayoutRatio: r.dividendPayoutRatioTTM || 0,
    // Company meta (from /profile — no extra call) — used for previews & ETF layout switch
    isEtf: p.isEtf || p.isFund || false,
    sector: p.sector || '',
    industry: p.industry || '',
    employees: p.fullTimeEmployees ? Number(p.fullTimeEmployees) || 0 : 0,
    ceo: p.ceo || '',
    companyName: p.companyName || '',
    exchange: p.exchange || p.exchangeShortName || '',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ★ Deep Analysis — lazy-loaded sections (fetched on demand when user expands)
// These endpoints are available on the current FMP plan but only called when
// the relevant collapsible card is opened, to minimize credit usage.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Financial Health scores — Piotroski (0-9) & Altman Z-Score
 * @param {string} symbol
 */
export async function getFinancialScores(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/financial-scores', { symbol: fmpSymbol });
  const item = Array.isArray(data) ? data[0] : data;
  if (!item) return null;
  return {
    altmanZScore: Number(item.altmanZScore) || 0,
    piotroskiScore: Number(item.piotroskiScore) || 0,
    workingCapital: Number(item.workingCapital) || 0,
    totalAssets: Number(item.totalAssets) || 0,
    totalLiabilities: Number(item.totalLiabilities) || 0,
    retainedEarnings: Number(item.retainedEarnings) || 0,
    revenue: Number(item.revenue) || 0,
    ebit: Number(item.ebit) || 0,
    marketCap: Number(item.marketCap) || 0,
  };
}

/**
 * Balance Sheet statement
 * @param {string} symbol
 * @param {string} period - 'annual' | 'quarter'
 * @param {number} limit
 */
export async function getBalanceSheet(symbol, period = 'annual', limit = 5) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/balance-sheet-statement', { symbol: fmpSymbol, period, limit });
  return Array.isArray(data) ? data : [];
}

/**
 * Cash Flow statement
 * @param {string} symbol
 * @param {string} period - 'annual' | 'quarter'
 * @param {number} limit
 */
export async function getCashFlowStatement(symbol, period = 'annual', limit = 5) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/cash-flow-statement', { symbol: fmpSymbol, period, limit });
  return Array.isArray(data) ? data : [];
}

/**
 * Key Metrics TTM (richer than ratios-ttm — EV multiples, ROIC, FCF, etc.)
 * @param {string} symbol
 */
export async function getKeyMetricsTTM(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/key-metrics-ttm', { symbol: fmpSymbol });
  const item = Array.isArray(data) ? data[0] : data;
  return item || null;
}

/**
 * Analyst grades consensus (Strong Buy / Buy / Hold / Sell / Strong Sell counts)
 * @param {string} symbol
 */
export async function getGradesConsensus(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/grades-consensus', { symbol: fmpSymbol });
  const item = Array.isArray(data) ? data[0] : data;
  if (!item) return null;
  return {
    strongBuy: Number(item.strongBuy) || 0,
    buy: Number(item.buy) || 0,
    hold: Number(item.hold) || 0,
    sell: Number(item.sell) || 0,
    strongSell: Number(item.strongSell) || 0,
    consensus: item.consensus || '',
  };
}

/**
 * Analyst grades historical (rating distribution over time)
 * @param {string} symbol
 * @param {number} limit
 */
export async function getGradesHistorical(symbol, limit = 12) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/grades-historical', { symbol: fmpSymbol, limit });
  return Array.isArray(data) ? data : [];
}

/**
 * Stock peers (competitors for comparison)
 * @param {string} symbol
 */
export async function getStockPeers(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/stock-peers', { symbol: fmpSymbol });
  return Array.isArray(data) ? data : [];
}

/**
 * Revenue segmentation — by product and by geography (latest period)
 * @param {string} symbol
 */
export async function getRevenueSegmentation(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  const [product, geo] = await Promise.all([
    fmpFetch('/revenue-product-segmentation', { symbol: fmpSymbol }).catch(() => []),
    fmpFetch('/revenue-geographic-segmentation', { symbol: fmpSymbol }).catch(() => []),
  ]);
  return {
    product: Array.isArray(product) ? product : [],
    geo: Array.isArray(geo) ? geo : [],
  };
}

/**
 * Key executives (management team)
 * @param {string} symbol
 */
export async function getKeyExecutives(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  const data = await fmpFetch('/key-executives', { symbol: fmpSymbol });
  return Array.isArray(data) ? data : [];
}

/**
 * ETF deep data — fund info, top holdings, sector weightings
 * @param {string} symbol
 */
export async function getEtfData(symbol) {
  const fmpSymbol = toFmpSymbol(symbol);
  const [info, holdings, sectors] = await Promise.all([
    fmpFetch('/etf/info', { symbol: fmpSymbol }).then(d => (Array.isArray(d) ? d[0] : d)).catch(() => null),
    fmpFetch('/etf/holdings', { symbol: fmpSymbol }).catch(() => []),
    fmpFetch('/etf/sector-weightings', { symbol: fmpSymbol }).catch(() => []),
  ]);
  return {
    info: info || null,
    holdings: Array.isArray(holdings) ? holdings : [],
    sectors: Array.isArray(sectors) ? sectors : [],
  };
}

/**
 * Get Insider Trading activity
 * @param {string} symbol
 * @param {number} limit
 */
export async function getInsiderTrading(symbol, limit = 10) {
  const fmpSymbol = toFmpSymbol(symbol);
  let data;
  try {
    data = await fmpFetch('/insider-trading', { symbol: fmpSymbol, limit });
  } catch {
    try {
      data = await fmpFetch('/insider-trades', { symbol: fmpSymbol, limit });
    } catch { return []; }
  }
  if (!Array.isArray(data)) return [];
  return data.map(item => ({
    date: item.filingDate || item.transactionDate || '',
    transactionDate: item.transactionDate || '',
    name: item.reportingName || item.reportingCik || '',
    title: item.typeOfOwner || '',
    transactionType: item.transactionType || item.acquistionOrDisposition || '',
    shares: item.securitiesTransacted || item.sharesTraded || 0,
    price: item.price || 0,
    value: (item.securitiesTransacted || item.sharesTraded || 0) * (item.price || 0),
  }));
}
